import React, { ReactElement, useCallback } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { colorsPalette } from '../../../../../shared/ui/styles/colors-palette';
import { useUILogic } from '../../../shared/providers/UILogicProvider/hooks';
import type { ConfirmationMethod } from '../../../../infrastructure/types/features/auth/auth-types';
import PhoneOutgoing from '../../../shared/assets/PhoneOutgoing.svg';
import PhoneIncoming from '../../../shared/assets/PhoneIncoming.svg';
import UiKit from '../../../../../shared/ui/ui-kit';

const METHOD_ORDER: ConfirmationMethod[] = ['outgoing', 'incoming', 'qr'];

const METHOD_LABEL_KEY: { [K in ConfirmationMethod]: string } = {
  outgoing: 'register.phone.confirm.method.outgoing',
  incoming: 'register.phone.confirm.method.incoming',
  qr: 'register.phone.confirm.method.qr',
};

interface PhoneMethodSelectorProps {}

let PhoneMethodSelector = function ({}: PhoneMethodSelectorProps): ReactElement {
  const { t } = useTranslation();
  const { authorizationUiModule } = useUILogic();
  const coreManager = authorizationUiModule.registerUIManager.coreManager;

  const handleSelect = useCallback(
    (method: ConfirmationMethod) => {
      coreManager.setConfirmationMethod(method);
    },
    [coreManager],
  );

  const otherMethods = METHOD_ORDER.filter(method => method !== coreManager.confirmationMethod);

  return (
    <Wrapper>
      <Title>{t('register.phone.confirm.method.title')}</Title>
      <Methods>
        {otherMethods.map(method => (
          <MethodButton key={method} type="button" onClick={() => handleSelect(method)}>
            {method === 'qr' ? (
              <UiKit.icons.QrcodeOutlined style={{ fontSize: 16, color: colorsPalette.gray600 }} />
            ) : method === 'incoming' ? (
              <Icon src={PhoneIncoming} style={{ fontSize: 16, color: colorsPalette.gray600 }} />
            ) : (
              <Icon src={PhoneOutgoing} style={{ fontSize: 16, color: colorsPalette.gray600 }} />
            )}
            <MethodLabel>{t(METHOD_LABEL_KEY[method])}</MethodLabel>
          </MethodButton>
        ))}
      </Methods>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;
const Title = styled.div`
  font-size: 12px;
  line-height: 20px;
  color: ${colorsPalette.gray600};
  text-align: center;
`;
const Methods = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;
`;
const MethodButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 15px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  background: ${colorsPalette.white};
  cursor: pointer;
`;
const MethodLabel = styled.span`
  font-size: 14px;
  line-height: 22px;
  color: ${colorsPalette.gray600};
`;

const Icon = styled.img``;

PhoneMethodSelector = observer(PhoneMethodSelector);

export { PhoneMethodSelector };
